/* ============================================================
   BW Intro - gekapselte Animationsschicht

   Die Sequenz laeuft ausschliesslich innerhalb ihres Containers.
   Sie blendet keine Geschwister aus und fasst das uebrige Dokument
   nur an einer einzigen Stelle an: der Scrollsperre (Option
   lockScroll, standardmaessig an). Deren Werte werden gemerkt und
   beim Ausblenden unveraendert zurueckgeschrieben.

   Ist der Merker im localStorage gesetzt, passiert gar nichts:
   kein Vermessen, keine Baender, keine Listener. Das
   Markup traegt bis dahin das Attribut hidden - solange es steht,
   ist die Schicht display:none und auch das Stylesheet tut nichts.

   Einbindung:

       import { initIntro } from "./js/intro.js";
       initIntro();

   Mit eigenen Werten:

       initIntro({ target: "#vorspann", lockScroll: false });
   ============================================================ */

/* Voreinstellungen. Direkt hier aendern oder pro Aufruf
   ueberschreiben - beides geht. */
export const DEFAULTS = {
    /* Element, Selektor oder null (dann ".bw-intro"). */
    target: ".bw-intro",

    /* Schluessel im localStorage. Eigener Name, falls mehrere
       Vorspaenne nebeneinander laufen sollen. */
    storeKey: "bw-intro-seen",

    /* Zum Testen auf false setzen: dann laeuft das Intro bei jedem
       Aufruf, ohne dass etwas gespeichert wird. */
    playOnce: true,

    /* Die Schicht liegt als fixierte Flaeche ueber dem gesamten
       Viewport und sperrt das Scrollen dahinter, solange sie
       laeuft. Zurueckgegeben wird die Seite exakt so, wie sie
       vorgefunden wurde - inklusive Scrollposition.

       Das ist die einzige Stelle, an der das Skript etwas
       ausserhalb des Containers anfasst. Auf false setzen, wenn
       die Seite dahinter scrollbar bleiben soll. */
    lockScroll: true
};

/* Beide Zugriffe sind gekapselt: in privaten Fenstern und bei
   blockierten Cookies wirft schon das Lesen. Faellt der Speicher
   aus, laeuft das Intro lieber einmal zu oft als gar nicht. */
export function hasIntroBeenSeen(storeKey) {
    const key = storeKey || DEFAULTS.storeKey;
    try { return window.localStorage.getItem(key) === "1"; }
    catch (e) { return false; }
}

export function resetIntro(storeKey) {
    const key = storeKey || DEFAULTS.storeKey;
    try { window.localStorage.removeItem(key); } catch (e) {}
}

/* ------------------------------------------------------------
   initIntro(options)

   options: Element, Selektor, Objekt oder nichts.
   ------------------------------------------------------------ */
export function initIntro(options) {
    const opts = Object.assign({}, DEFAULTS,
        options && typeof options === "object" && !(options instanceof Element)
            ? options
            : { target: options || DEFAULTS.target });

    /* Wer das Skript ohne type="module" und ohne defer einbindet,
       ruft initIntro() auf, bevor das Markup im Dokument steht.
       Statt still nichts zu tun, wird dann kurz gewartet. */
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () { start(opts); }, { once: true });
    } else {
        start(opts);
    }
}

function start(opts) {
    "use strict";

    const root =
        opts.target instanceof Element ? opts.target :
            typeof opts.target === "string" ? document.querySelector(opts.target) :
                document.querySelector(DEFAULTS.target);

    if (!root) { return; }

    /* --- Erste und einzige Frage --------------------------------------
       Sie steht vor jeder anderen Zeile. Wer das Intro schon gesehen hat,
       loest hier keinen einzigen Nebeneffekt mehr aus - kein Lesen von
       getComputedStyle, kein addEventListener, kein Anfassen des Tons.

       Angefasst wird auch das Markup nicht: das Attribut hidden steht
       bereits im Dokument, die Schicht ist damit display:none und kostet
       weder Layout noch Farbe. Nichts zu tun ist hier die vollstaendige
       Massnahme. */
    if (opts.playOnce && hasIntroBeenSeen(opts.storeKey)) { return; }

    function markSeen() {
        if (!opts.playOnce) { return; }
        try { window.localStorage.setItem(opts.storeKey, "1"); } catch (e) {}
    }

    const SVGNS = "http://www.w3.org/2000/svg";

    /* --- Entwurfsraster und sichtbarer Ausschnitt ---------------------
       Gesetzt ist der Schriftzug in einem festen Raster von 1280 x 720.
       Alle Abstaende, Grade und Flugbahnen beziehen sich darauf und
       bleiben unveraendert - das ist die Gestaltung.

       Der sichtbare Ausschnitt (die viewBox) ist davon getrennt: er
       folgt dem Seitenverhaeltnis des Fensters und waechst auf der
       Achse, die mehr Platz hat. Damit fuellt die Szene den Viewport
       ohne Leerbalken, und weil das Raster im Ausschnitt zentriert
       aufgehaengt wird, steht der Schriftzug in jedem Format exakt in
       der Mitte - waagerecht wie senkrecht.

       Frueher waren beide dasselbe: viewBox 1280 x 720 mit
       preserveAspectRatio="xMidYMid meet". Auf einem hohen Telefon
       stand das 16:9-Feld dann als Briefkasten mitten im Bild, und
       Schein und Vignette (CSS, also am Viewport ausgerichtet) liefen
       gegen die Mitte des SVG. */
    const DESIGN_W = 1280, DESIGN_H = 720;
    let VW = DESIGN_W, VH = DESIGN_H;

    /* --- Bausteine, alle relativ zum Container ------------------------
       Gesucht wird ueber data-Attribute statt ueber IDs: die Schicht ist
       ein Bestandteil der Seite und darf keine allgemeinen Namen wie
       "stage" fuer sich beanspruchen. */
    const svg      = root.querySelector("[data-bw-stage]");
    const defs     = root.querySelector("[data-bw-defs]");
    const blooms   = root.querySelector("[data-bw-blooms]");
    const letters  = root.querySelector("[data-bw-letters]");
    const measure  = root.querySelector("[data-bw-measure]");
    const glow     = root.querySelector("[data-bw-glow]");

    if (!svg || !defs || !blooms || !letters || !measure || !glow) { return; }

    /* Alle im Skript erzeugten IDs bekommen ein eigenes Praefix, ebenso
       die beiden festen aus dem Markup. Damit kollidiert nichts mit der
       uebrigen Seite, und die Schicht liesse sich auch mehrfach
       einbauen. */
    const uid       = "bw-" + Math.random().toString(36).slice(2, 8);
    const BLOOM_ID  = uid + "-bloom";
    const DEPTH_ID  = uid + "-depth";

    const bloomFilter   = svg.querySelector("[data-bw-bloom-filter]");
    const depthGradient = svg.querySelector("[data-bw-depth-gradient]");
    if (bloomFilter)   { bloomFilter.id   = BLOOM_ID; }
    if (depthGradient) { depthGradient.id = DEPTH_ID; }

    /* --- Ausschnitt an den Viewport anpassen --------------------------
       Setzt VW/VH auf das Seitenverhaeltnis des Containers und haengt
       das Entwurfsraster mittig hinein. Die Verschiebung liegt an den
       beiden Zeichengruppen, nicht an den Einzelteilen: die clipPaths
       werden von Elementen innerhalb dieser Gruppen referenziert und
       wandern deshalb mit.

       Aufgerufen wird die Funktion vor dem ersten Frame und danach bei
       jeder Groessenaenderung. Neu vermessen oder aufgebaut wird dabei
       nichts - es aendern sich nur zwei Zahlen und ein transform. */
    function measureViewport() {
        const box = root.getBoundingClientRect();
        const w = box.width  || window.innerWidth  || DESIGN_W;
        const h = box.height || window.innerHeight || DESIGN_H;
        const ratio = w / h;

        if (!isFinite(ratio) || ratio <= 0) { return; }

        if (ratio >= DESIGN_W / DESIGN_H) {
            /* Breiter als das Raster: Hoehe bleibt, Breite waechst. */
            VH = DESIGN_H;
            VW = Math.round(DESIGN_H * ratio);
        } else {
            /* Hoeher als das Raster: Breite bleibt, Hoehe waechst. */
            VW = DESIGN_W;
            VH = Math.round(DESIGN_W / ratio);
        }

        const shift = "translate(" +
            Math.round((VW - DESIGN_W) / 2) + "," +
            Math.round((VH - DESIGN_H) / 2) + ")";

        blooms.setAttribute("transform", shift);
        letters.setAttribute("transform", shift);
    }

    /* Drehen, Fenstergroesse, ein- und ausfahrende Browserleisten auf dem
       Telefon: alles aendert nur den Ausschnitt. Gebuendelt auf einen
       Frame, damit eine Serie von resize-Ereignissen nicht eine Serie von
       Attributschreibungen ausloest. */
    function onResize() {
        if (dismissed) { return; }
        window.cancelAnimationFrame(resizeRaf);
        resizeRaf = window.requestAnimationFrame(function () {
            measureViewport();
            if (camRefresh) {
                /* Kamera laeuft: sie setzt die viewBox im naechsten Frame
                   selbst, mit dem Zoomwert des aktuellen Zeitpunkts. */
                camRefresh();
            } else {
                svg.setAttribute("viewBox", "0 0 " + VW + " " + VH);
            }
        });
    }

    /* --- Scrollsperre --------------------------------------------------------
       Steht bewusst vor dem Sichtbarmachen: ab der naechsten Zeile deckt
       die Schicht den Viewport, und alles, was dahinter noch scrollen
       kann, scrollt unsichtbar. Frueher fiel die Sperre erst mit
       reveal(), also erst nachdem die Schriften eingetroffen waren - bis
       zu FONT_TIMEOUT (1200 ms) blieb die Seite hinter der schwarzen
       Flaeche beweglich.

       Nur aktiv, wenn lockScroll gesetzt ist (Voreinstellung: an). Das
       ist die einzige Stelle, an der das Skript etwas ausserhalb des
       Containers anfasst - deshalb wird jeder veraenderte Wert vorher
       gemerkt und beim Aufraeumen genau so zurueckgeschrieben. Ein
       Layout, das selbst schon etwas an overflow gedreht hat, bleibt
       damit unbeschaedigt.

       overflow: hidden auf html und body haelt die Scrollposition,
       anders als der verbreitete Trick mit position: fixed am body - der
       springt beim Loesen nach oben, wenn man die Position nicht selbst
       zurueckrechnet. Inline notiert, damit es auch gegen
       Projektstylesheets steht (nur ein !important im Projekt koennte
       es noch schlagen).

       Fuenf Massnahmen, weil eine allein nicht ueberall reicht:

         1. overflow: hidden - greift auf dem Desktop und unter Android.
         2. Breitenausgleich - faellt die Bildlaufleiste weg, wuerde der
            Seiteninhalt dahinter um deren Breite springen. Sichtbar wird
            das im Moment des Ausblendens.
         3. touchmove abfangen - iOS Safari scrollt trotz overflow:
            hidden weiter. Dazu kommt touch-action: none aus dem
            Stylesheet.
         4. wheel abfangen - fuer Layouts, die ihren Bildlauf nicht am
            Dokument, sondern in einem eigenen Container fuehren; dort
            greift overflow: hidden am body nicht. Strg + Rad bleibt
            frei, das ist die Browserzoomstufe und gehoert dem Besucher.
         5. Tasten abfangen - Leertaste, Bild auf/ab, Pos1/Ende und die
            Pfeile. In der Schicht gibt es nichts zu bedienen, also geht
            dabei nichts verloren. Modifizierte Anschlaege bleiben frei. */
    const SCROLL_KEYS = {
        " ": 1, "Spacebar": 1, "PageUp": 1, "PageDown": 1,
        "End": 1, "Home": 1,
        "ArrowUp": 1, "ArrowDown": 1, "ArrowLeft": 1, "ArrowRight": 1
    };

    let scrollLocked = false;
    let prevHtmlOverflow = "";
    let prevBodyOverflow = "";
    let prevBodyPadRight = "";

    function blockTouch(e) { e.preventDefault(); }

    function blockWheel(e) {
        if (e.ctrlKey) { return; }
        e.preventDefault();
    }

    function blockKeys(e) {
        if (e.ctrlKey || e.metaKey || e.altKey) { return; }
        if (SCROLL_KEYS[e.key]) { e.preventDefault(); }
    }

    function lockScroll() {
        if (!opts.lockScroll || scrollLocked) { return; }
        scrollLocked = true;

        const html = document.documentElement;
        const body = document.body;

        /* Vor dem Sperren messen: danach ist die Leiste weg und die
           Differenz waere null. */
        const gap = window.innerWidth - html.clientWidth;

        prevHtmlOverflow = html.style.overflow;
        prevBodyOverflow = body.style.overflow;
        prevBodyPadRight = body.style.paddingRight;

        html.style.overflow = "hidden";
        body.style.overflow = "hidden";

        if (gap > 0) {
            const cur = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
            body.style.paddingRight = (cur + gap) + "px";
        }

        document.addEventListener("touchmove", blockTouch, { passive: false });
        window.addEventListener("wheel", blockWheel, { passive: false });
        window.addEventListener("keydown", blockKeys, false);
    }

    function unlockScroll() {
        if (!scrollLocked) { return; }
        scrollLocked = false;

        document.removeEventListener("touchmove", blockTouch, { passive: false });
        window.removeEventListener("wheel", blockWheel, { passive: false });
        window.removeEventListener("keydown", blockKeys, false);

        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.paddingRight = prevBodyPadRight;
    }

    /* --- Tokens aus dem Stylesheet ------------------------------------
       Farben und Zeiten stehen in intro.scss und liegen als Custom
       Properties auf .bw-intro selbst. Sie hier noch einmal zu notieren
       hiesse, sie an zwei Stellen zu pflegen - und der Kontrastmodus
       wuerde nur an einer davon greifen. Die Fallbacks sind reine
       Notnaegel fuer den Fall, dass das Stylesheet fehlt.

       Gelesen wird vor root.hidden = false. Custom Properties liefert
       getComputedStyle auch an einem display:none Element - es braucht
       dafuer kein Layout, und Vermessen findet hier ohnehin nicht
       statt. Der Grund steht damit fest, bevor die Schicht das erste
       Mal in den Fluss kommt. */
    const css = window.getComputedStyle(root);

    function token(name, fallback) {
        const v = css.getPropertyValue(name).trim();
        return /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
    }
    function tokenMs(name, fallback) {
        const v = css.getPropertyValue(name).trim();
        if (/ms$/.test(v)) { return parseFloat(v); }
        if (/s$/.test(v))  { return parseFloat(v) * 1000; }
        return fallback;
    }

    /* --- Der Grund ----------------------------------------------------
       Die Schicht muss decken. Eine durchsichtige Vollbildflaeche ist
       nicht "fast richtig", sondern die Seite selbst mit einer
       Animation darueber - und in einem hellen Projekt sieht man das
       sofort.

       Der Grund ist festgelegt: #00101E, immer. Er stand hier frueher als
       Token, gelesen aus --bw-intro-bg, mit einer Gueltigkeitspruefung
       davor und einem Notnagel dahinter - eine ganze Apparatur fuer eine
       Farbe, die sich nicht aendern soll.

       Frei war sie ohnehin nie: derselbe Ton steckt zweimal fest in der
       Gestaltung - in der Tiefenblende ueber den Buchstaben (im Markup)
       und in der Vignette (im Stylesheet). Wer den Token umsetzte, bekam
       keinen neuen Grund, sondern einen Grund, der nicht mehr zu seinen
       eigenen Verlaeufen passte: die Vignette lief nach aussen weiterhin
       auf Blauschwarz zu, sichtbar als Ring auf der neuen Farbe.

       Mit dem Literal faellt die halbe Apparatur weg. Die Pruefung, weil
       eine feste Farbe nicht ungueltig sein kann; der Notnagel, weil es
       nichts mehr gibt, was ausfallen koennte.

       Gesetzt wird weiterhin inline - der Inline-Stil schlaegt jede
       Projektregel -, jetzt zusaetzlich mit important, damit auch eine
       Projektregel mit important nicht mehr durchkommt. Das war die
       letzte Luecke, durch die der Grund noch kippen konnte.

       Anders als bei der Scrollsperre wird hier nichts zurueckgesetzt:
       angefasst wird das eigene Element der Schicht, und das ist am
       Ende ohnehin display:none.

       Dieselbe Farbe steht ein zweites Mal im Stylesheet. Sie deckt den
       Fall ab, dass dieses Skript gar nicht erst laeuft. */
    root.style.setProperty("background-color", "#00101E", "important");

    /* Und ein zweites Mal als eigene Ebene in der Buehne. Die Farbe oben
       liegt am Container, also unter allem, was Buehne, Pseudoelemente
       oder eine Projektregel mit background-image darueber malen. Die
       Ebene liegt im Stapel der Buehne und laesst sich nicht
       hinterlaufen.

       Sie ersetzt die Farbe am Container nicht: die Buehne wird
       eingeblendet, deckt also erst nach dem Reveal. Bis dahin traegt
       der Container.

       Fehlt das Element im Markup, passiert hier nichts - die Schicht
       laeuft dann wie zuvor mit der Farbe am Container. */
    const ground = root.querySelector("[data-bw-ground]");
    if (ground) { ground.style.setProperty("background-color", "#00101E", "important"); }

    /* Ab hier wird gezeichnet, also darf die Schicht in den Fluss. Der
       Grund steht bereits als Inline-Stil am Element - die Flaeche deckt
       damit vom ersten Frame an, unabhaengig davon, was das Stylesheet
       zu dieser Farbe sagt oder nicht sagt. */
    root.hidden = false;

    /* Sperren, sobald die Flaeche steht - nicht erst, wenn die Schriften
       da sind. */
    lockScroll();

    /* Ausschnitt sofort auf das Fensterformat bringen: der erste Frame
       soll bereits im richtigen Format stehen. */
    measureViewport();
    svg.setAttribute("viewBox", "0 0 " + VW + " " + VH);

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const BRAND = token("--bw-intro-brand", "#B70E0C");
    const RAMP  = [
        token("--bw-intro-ramp-0", "#2E0403"),
        token("--bw-intro-ramp-1", "#7C0A08"),
        token("--bw-intro-ramp-2", BRAND),
        token("--bw-intro-ramp-3", "#E85A45")
    ];

    /* Zweiter Farbsatz fuer "weekly". Die Baender starten auf gedeckten
       Blaugrautoenen aus der Naehe des Grundes und laufen auf Weiss zu -
       derselbe Vorgang wie beim roten Wort, nur in der anderen Familie. */
    const ACCENT = token("--bw-intro-accent", "#FFFFFF");
    const ACCENT_RAMP = [
        token("--bw-intro-accent-0", "#4A5A66"),
        token("--bw-intro-accent-1", "#8C9BA6"),
        token("--bw-intro-accent-2", "#CBD5DA"),
        token("--bw-intro-accent-3", ACCENT)
    ];

    const FADE = tokenMs("--bw-intro-fade-duration", 650);

    const FONT_FULL = "Roboto, 'Helvetica Neue', Arial, sans-serif";
    const FONT_BACK = "'Helvetica Neue', Arial, sans-serif";
    let   FONT = FONT_FULL;

    /* --- Der Schriftzug ----------------------------------------------------
       Jeder Eintrag ist eine eigene Einheit: eigene clipPath, eigene
       Baender, eigener Bloom, eigener Lichtstreifen. Gestaffelt wird
       zwischen den Woertern, nicht zwischen den Buchstaben - bei 13
       Zeichen ergaebe eine Staffelung pro Glyphe eine Laufzeit von ueber
       einer Minute, und der Aufbau zerfiele in Einzelteile statt als
       Schriftzug zu erscheinen.

       Innerhalb eines Wortes bleibt die Staffelung erhalten: die Baender
       laufen von der Wortmitte nach aussen gestaffelt ein, wie vorher
       innerhalb eines Buchstabens. */
    const WORDS = [
        {
            text:   "BUCHNER",
            /* Halbfett und deutlich kleiner: die Zeile nennt den Absender,
               "weekly" traegt das Zeichen. 0.48 ist ihr Anteil am
               Grundgrad. */
            scale:  0.4,
            weight: 600,
            style:  "normal",
            tint:   ACCENT,
            ramp:   ACCENT_RAMP
        },
        {
            /* Kleinbuchstaben und Kursive stehen im Markup, nicht in CSS:
               text-transform greift auf SVG-Text nicht zuverlaessig, und
               vermessen wird ohnehin die tatsaechliche Glyphenfolge. */
            text:   "weekly",
            scale:  0.8,
            weight: 700,
            style:  "italic",
            tint:   BRAND,
            ramp:   RAMP
        }
    ];

    /* Groesster Grad im Satz. Die Einpassung weiter unten bezieht ihren
       Spielraum darauf, damit scale ein absoluter Regler bleibt: wer
       "weekly" von 1 auf 0.9 nimmt, bekommt eine um ein Zehntel kleinere
       Zeile. Ohne diesen Bezug rechnete die Einpassung den Verlust sofort
       wieder heraus - sie fuellt ja immer denselben Anteil der Flaeche -
       und statt eines kleineren "weekly" stuende dort ein groesseres
       "BUCHNER". */
    const MAX_SCALE = WORDS.reduce(function (m, w) {
        return Math.max(m, w.scale);
    }, 0) || 1;

    /* Groesse des gesamten Blocks, als Anteil des Entwurfsrasters. Der
       Schriftzug wird so eingepasst, dass er hoechstens diesen Anteil der
       Breite und diesen Anteil der Hoehe belegt - was zuerst anschlaegt,
       gewinnt. Bei zwei Zeilen ist das in aller Regel die Breite.

       Das ist der Regler fuer beide Woerter gemeinsam. scale in WORDS
       regelt nur ihr Verhaeltnis zueinander: beide dort um denselben
       Faktor zu verkleinern bliebe folgenlos, weil MAX_SCALE mitwandert
       und die Einpassung den Block anschliessend wieder auf denselben
       Anteil zieht.

       Vorher standen hier 0.78 und 0.72. */
    const FILL_W = 0.624;   // 0.78 * 0.8
    const FILL_H = 0.576;   // 0.72 * 0.8

    /* Abstand zwischen Unterkante der oberen und Oberkante der unteren
       Zeile, als Anteil des Grundgrads. Gemessen wird optisch an den
       tatsaechlichen Glyphenkanten und nicht ueber einen festen
       Grundlinienabstand: bei zwei verschiedenen Schriftgraden liefe ein
       fester Zeilenabstand je nach Groessenverhaeltnis auseinander.

       Der Wert ist negativ: die Oberlaengen von "k" und "l" schieben sich
       ein Stueck ueber die Grundlinie von "BUCHNER" hinauf, die Zeilen
       greifen also leicht ineinander. Weil "BUCHNER" aus Versalien ohne
       Unterlaengen besteht, treffen sie dabei nicht aufeinander - der
       Ueberschnitt faellt in die Zwischenraeume der oberen Zeile.

       Achtung beim Weiterdrehen: ab etwa -0.06 laufen die Oberlaengen in
       die Versalien hinein. Ueberdeckt wird dann "BUCHNER", weil "weekly"
       spaeter aufgebaut wird und damit obenauf liegt. */
    const LINE_GAP = -0.02;

    /* ======================================================================
       TIMING - Gesamtdauer 3000 ms

         BUCHNER baut auf    0 - 1050
         weekly baut auf   520 - 1600     (WORD_OFFSET nach BUCHNER)
         Stand            1600 - 2050     Standbild, bewegungslos
         Beide loesen     2050 - 3000     auf, von aussen nach innen

       Ende = max ueber alle Phasen. Die Aufloesung ist der spaeteste Wert:
         EXIT_START + EXIT_SPREAD + EXIT_DUR = 2050 + 150 + 800 = 3000
       ====================================================================== */
    const TOTAL = 3000;

    const WORD_OFFSET = 520;

    const BUILD_STAGGER = 16;
    const BUILD_DUR     = 780;
    const BUILD_JITTER  = 30;

    const TINT_OFFSET = 300;
    const TINT_STEP   = 8;
    const TINT_DUR    = 600;

    const BLOOM_OFFSET = 320;
    const SWEEP_OFFSET = 240;
    const SWEEP_DUR    = 620;

    const EXIT_START  = 2050;
    const EXIT_SPREAD = 150;
    const EXIT_DUR    = 800;

    const CAM_FROM = 0.42;
    const CAM_HOLD = 1.0;
    const CAM_END  = 1.62;

    /* Nach dem letzten Frame kurz warten, dann ausblenden. Das Bild ist zu
       diesem Zeitpunkt bereits leer - die Baender sind weg. */
    const END_HOLD = 150;

    /* Bei reduzierter Bewegung steht nur das fertige Wortzeichen. Die volle
       Laufzeit abzuwarten haette dort keinen Zweck. */
    const REDUCED_HOLD = 1200;

    /* Notbremse fuer die Schriften. Kurz gehalten: das Intro haengt an
       nichts anderem mehr, und dank freezeFont() laeuft es nach Ablauf
       sauber mit der Ersatzschrift statt halb umzuspringen. */
    const FONT_TIMEOUT = 1200;

    /* Streifenbreite in viewBox-Einheiten. Hoeher gesetzt bedeutet weniger
       Baender: bei 40 statt 30 sind es rund ein Viertel weniger Elemente
       und damit ein Viertel weniger Keyframe-Animationen. Der Aufbau wirkt
       dann etwas grober. */
    const BAR_WIDTH = 30;

    const EASE_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";
    const EASE_IN  = "cubic-bezier(0.7, 0, 0.84, 0)";
    const SOFT     = "cubic-bezier(0.4, 0, 0.2, 1)";

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let started = false;
    let revealed = false;
    let dismissed = false;
    let rafId = 0;
    let dismissTimer = 0;
    let loadTimer = 0;
    let resizeRaf = 0;
    let camRefresh = null;

    const bars = [];
    const units = [];
    let WORD_FORM = 0;
    let WORD_MID = DESIGN_W / 2;
    let WORD_HALF = DESIGN_W / 2;

    /* --- Hilfen ------------------------------------------------------------ */
    function el(name, attrs) {
        const n = document.createElementNS(SVGNS, name);
        for (const k in attrs) { n.setAttribute(k, attrs[k]); }
        return n;
    }
    function clearAll(node) {
        if (typeof node.getAnimations !== "function") { return; }
        node.getAnimations().forEach(function (a) { a.cancel(); });
    }
    function clamp01(v) { return Math.min(1, Math.max(0, v)); }

    /* Auswertung einer CSS-Bezierkurve in JS. Gebraucht, weil die Kamera
       nicht ueber die Web Animations API laeuft, aber exakt dieselben Kurven
       benutzen muss wie die Baender - sonst laufen Zufahrt und Einflug
       sichtbar auseinander. Newton-Iteration, 8 Schritte. */
    function bezier(x1, y1, x2, y2) {
        function A(a, b) { return 1 - 3 * b + 3 * a; }
        function B(a, b) { return 3 * b - 6 * a; }
        function C(a)    { return 3 * a; }
        function calc(t, a, b)  { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
        function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
        return function (x) {
            if (x <= 0) { return 0; }
            if (x >= 1) { return 1; }
            let t = x;
            for (let i = 0; i < 8; i++) {
                const s = slope(t, x1, x2);
                if (s === 0) { break; }
                t -= (calc(t, x1, x2) - x) / s;
            }
            return calc(t, y1, y2);
        };
    }
    const easeOutFn = bezier(0.16, 1, 0.3, 1);
    const easeInFn  = bezier(0.7, 0, 0.84, 0);

    /* Schrift einmal festnageln, bevor irgendetwas vermessen wird. Trifft
       Roboto spaeter ein, wuerde sonst die clipPath neu aufgebaut und die
       Wortform springt mitten in der Sequenz um. Bei font-display: block
       haetten die Glyphen vorher gar keine Kontur - die Maske waere leer
       und der Schriftzug kurz unsichtbar.

       Geprueft werden beide gebrauchten Schnitte, Halbfett aufrecht und
       Fett kursiv. Fehlte einer, wuerde der Browser ihn aus einem anderen
       synthetisieren - schraeggestellt oder kuenstlich verfettet - und die
       Zeile liefe schmaler oder breiter als gemessen. */
    function freezeFont() {
        const ok = document.fonts && document.fonts.check &&
            document.fonts.check("600 340px Roboto") &&
            document.fonts.check("italic 700 340px Roboto");
        FONT = ok ? FONT_FULL : FONT_BACK;
        measure.setAttribute("font-family", FONT);
    }

    /* Deterministischer Pseudozufall: gleiche Eingabe, gleiches Ergebnis.
       Streut Distanz, Drift und Verzoegerung, damit die Baender organisch
       statt mechanisch laufen - aber bei jedem Abspielen identisch. Fuer ein
       Ident ist Reproduzierbarkeit wichtiger als echter Zufall. */
    function rnd(i) {
        const x = Math.sin((i + 1) * 12.9898) * 43758.5453;
        return x - Math.floor(x);
    }

    function toRgb(c) {
        return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
    }
    function mix(a, b, t) {
        const A = toRgb(a), B = toRgb(b);
        let out = "#";
        for (let i = 0; i < 3; i++) {
            const v = Math.round(A[i] + (B[i] - A[i]) * t).toString(16);
            out += v.length < 2 ? "0" + v : v;
        }
        return out;
    }
    /* Farbverlauf innerhalb eines Farbsatzes. Der Satz wird uebergeben,
       damit rotes und weisses Wort dieselbe Mechanik teilen. */
    function ramp(list, t) {
        const seg = (list.length - 1) * clamp01(t);
        const i = Math.min(Math.floor(seg), list.length - 2);
        return mix(list[i], list[i + 1], seg - i);
    }

    /* --- Aufbau des Schriftzugs --------------------------------------------
       Jedes Wort bekommt eine eigene clipPath, damit es unabhaengig
       animiert werden kann.

       Die Woerter stehen untereinander, jedes auf eigener Grundlinie und
       fuer sich zentriert. Positioniert wird ueber
       getComputedTextLength(), zentriert ueber die Tinten-Bounding-Box.
       text-anchor allein wuerde die Seitenraender der Glyphen mitzaehlen
       und die Zeilen gegeneinander verschieben.

       Gemessen wird pro Zeile mit deren eigenem Schnitt: "BUCHNER"
       halbfett aufrecht, "weekly" fett kursiv. Eine gemeinsame Messung
       waere falsch, die Kursive laeuft breiter als ihr Vorschub angibt. */
    function layout() {
        freezeFont();

        /* Der Ausschnitt kann sich zwischen dem ersten Messen und dem
           Start geaendert haben (Drehen des Geraets, Schriftwartezeit). */
        measureViewport();

        let size = 340;

        function useFont(word) {
            measure.setAttribute("font-family", FONT);
            measure.setAttribute("font-size", size * word.scale);
            measure.setAttribute("font-weight", word.weight);
            measure.setAttribute("font-style", word.style);
        }

        function metrics(word) {
            useFont(word);
            measure.textContent = word.text;
            return { adv: measure.getComputedTextLength(), box: measure.getBBox() };
        }

        function measureAll() {
            const boxes = WORDS.map(metrics);
            const gap = size * LINE_GAP;

            /* Grundlinien relativ zur ersten Zeile. Jede Zeile haengt an der
               Unterkante der vorigen: baseline[i] so, dass die Oberkante von
               i genau LINE_GAP unter der Unterkante von i-1 liegt. box.y ist
               dabei negativ - es ist der Abstand der Oberkante ueber der
               eigenen Grundlinie. */
            const rel = [0];
            for (let i = 1; i < boxes.length; i++) {
                const prev = boxes[i - 1].box;
                rel.push(rel[i - 1] + prev.y + prev.height - boxes[i].box.y + gap);
            }

            let widest = 0, top = Infinity, bottom = -Infinity;
            boxes.forEach(function (mm, i) {
                if (mm.adv > widest) { widest = mm.adv; }
                const t = rel[i] + mm.box.y;
                const b = t + mm.box.height;
                if (t < top) { top = t; }
                if (b > bottom) { bottom = b; }
            });

            return { boxes: boxes, rel: rel, widest: widest, top: top, height: bottom - top };
        }

        let m = measureAll();

        /* Der Block ist hoch statt breit: die Breite richtet sich nach der
           laengsten Zeile, die Hoehe nach Oberkante der Versalien bis
           Unterlaenge der letzten Zeile.

           Der Spielraum ist mit MAX_SCALE gewichtet. Bei einem Satz, in
           dem ein Wort auf scale 1 steht, aendert das nichts - sonst
           schrumpft der Anteil mit, und ein kleiner gesetztes Wort bleibt
           auch auf dem Schirm kleiner. */
        if (m.widest > 0 && m.height > 0) {
            const fit = Math.min(
                (DESIGN_W * FILL_W * MAX_SCALE) / m.widest,
                (DESIGN_H * FILL_H * MAX_SCALE) / m.height
            );
            size = Math.round(size * fit);
            m = measureAll();
        }

        const baseY = Math.round(DESIGN_H / 2 - m.top - m.height / 2);

        WORDS.forEach(function (word, idx) {
            const adv = m.boxes[idx].adv;
            const box = m.boxes[idx].box;
            const baseline = Math.round(baseY + m.rel[idx]);
            const clipId = uid + "-clip" + idx;

            /* Jede Zeile fuer sich zentriert. Wer die Zeilen stattdessen
               rechtsbuendig unter die erste setzen moechte, ersetzt das hier
               durch penX = DESIGN_W / 2 + m.widest / 2 - adv. */
            const penX = Math.round(DESIGN_W / 2 - adv / 2);

            const clip = el("clipPath", { id: clipId });
            clip.appendChild(el("text", {
                x: penX, y: baseline,
                "text-anchor": "start",
                "font-family": FONT,
                "font-weight": word.weight,
                "font-style": word.style,
                "font-size": size * word.scale
            })).textContent = word.text;
            defs.appendChild(clip);

            /* --- Das Feld der Zeile ---------------------------------------
               Alles, was die Glyphen faerbt - Baender wie Bloom -, wird
               innerhalb dieses Feldes aufgespannt. Es ist breiter als die
               Zeile, und zwar deutlich breiter, als es frueher war.

               Der Vorschub sagt, wo die naechste Glyphe ansetzt - nicht,
               wie weit die Tinte reicht. Bei der Kursiven laeuft sie oben
               rechts darueber hinaus: die Fahne des "y" steht ueber ihrem
               eigenen Vorschub, unten links kippt der Abstrich in die
               Gegenrichtung. Mit einem Zuschlag von 10 Einheiten endete
               das Feld mitten in der Fahne. Die clipPath gab die Glyphe
               dort weiterhin frei, nur lag dahinter kein Band mehr, das
               sie haette faerben koennen - und eine Glyphe ohne Farbe ist
               von einer abgeschnittenen nicht zu unterscheiden. Genau das
               war oben rechts am "y" zu sehen.

               Nachgemessen wird der Ueberhang nicht: getBBox() liefert fuer
               Text den Vorschub und die Schriftmasse, nicht die Tinte -
               also dieselbe Groesse, die den Fehler verursacht hat. Wie weit
               die Tinte darueber hinaussteht, haengt am Schnitt und an der
               Ersatzschrift, wenn Roboto nicht eintrifft. Deshalb ein
               Zuschlag, der jede uebliche Kursive abdeckt. Zu breit zu sein
               kostet hier nichts: was ausserhalb der clipPath liegt, ist
               unsichtbar.

               Der Zuschlag haengt am Grad der Zeile, nicht an einer festen
               Zahl - sonst schruempfte er auf kleinen Schirmen mit, waehrend
               der Ueberhang, der ebenfalls am Grad haengt, bliebe. */
            const pad = Math.round(size * word.scale * 0.3);
            const x0  = penX - pad;
            const w   = adv + pad * 2;

            /* Bloom: geclippte Flaeche, weichgezeichnet. Das Rect deckt nur
               diese Zeile ab - eine clipPath verkleinert die Bounding-Box
               nicht, ein bildfuellendes Rect muesste sonst ueber die volle
               Flaeche weichgezeichnet werden, jeden Frame neu.

               Die Farbe folgt dem Wort: die rote Zeile glueht rot, die
               weisse weiss. Ein gemeinsamer roter Schein haette "weekly"
               einen Farbstich gegeben. */
            const bloomG = el("g", { filter: "url(#" + BLOOM_ID + ")", opacity: 0 });
            const bloomInner = el("g", { "clip-path": "url(#" + clipId + ")" });
            bloomInner.appendChild(el("rect", {
                x: x0 - 40,
                y: Math.round(baseline + box.y - 40),
                width:  Math.round(w + 80),
                height: Math.round(box.height + 80),
                fill: word.tint
            }));
            bloomG.appendChild(bloomInner);
            blooms.appendChild(bloomG);

            const group = el("g", { "clip-path": "url(#" + clipId + ")" });
            const ribbons = el("g", {});
            group.appendChild(ribbons);

            /* Der Lichtstreifen folgt der Zeilenfarbe: warm ueber der roten,
               neutral ueber der weissen. Ein einheitlich warmer Streifen
               zoege einen rosa Schimmer ueber die weisse Zeile. */
            const sweepTint = word.tint === BRAND ? "#FFC9C1" : "#DCE4EA";
            const sweepG = el("g", {});
            sweepG.appendChild(el("rect", { x: -520, y: -200, width: 118, height: 1120, fill: sweepTint, opacity: 0.16, transform: "skewX(-14)" }));
            sweepG.appendChild(el("rect", { x: -378, y: -200, width: 22, height: 1120, fill: "#FFFFFF", opacity: 0.12, transform: "skewX(-14)" }));
            group.appendChild(sweepG);

            group.appendChild(el("rect", { x: 0, y: 0, width: DESIGN_W, height: DESIGN_H, fill: "url(#" + DEPTH_ID + ")" }));
            letters.appendChild(group);

            /* Baenderdichte an der Feldbreite statt an einer festen Zahl:
               die laengere Zeile bekommt mehr Baender, damit die
               Streifenbreite ueber beide Zeilen gleich bleibt. */
            const count = Math.max(14, Math.round(w / BAR_WIDTH));
            const barW = w / count;
            const mid = (count - 1) / 2;

            for (let i = 0; i < count; i++) {
                const cx = x0 + i * barW + barW / 2;

                /* Der Verlauf spannt sich ueber die jeweilige Zeile, nicht
                   ueber die Bildbreite: sonst bekaeme die kuerzere Zeile nur
                   den mittleren Ausschnitt des Farbsatzes zu sehen.

                   Bezug ist die Zeile, nicht das Feld. Sonst schoebe der
                   Zuschlag oben die Farben nach innen, und der Verlauf
                   liefe erst innerhalb des Wortzeichens los. Die Baender
                   im Zuschlag bekommen den jeweiligen Randton - ramp()
                   klemmt t selbst auf 0 bis 1. */
                const t = adv > 0 ? (cx - penX) / adv : 0.5;

                const bar = el("rect", {
                    x: x0 + i * barW, y: -300,
                    width: barW + 1, height: DESIGN_H + 600,
                    fill: ramp(word.ramp, t)
                });
                ribbons.appendChild(bar);
                bars.push({
                    node: bar,
                    seed: idx * 101 + i,
                    dist: Math.abs(i - mid),
                    centerX: cx,
                    start: idx * WORD_OFFSET,
                    from: ramp(word.ramp, t),
                    tint: word.tint
                });
            }

            units.push({
                bloom: bloomG,
                sweep: sweepG,
                start: idx * WORD_OFFSET,
                form:  idx * WORD_OFFSET + mid * BUILD_STAGGER + BUILD_JITTER + BUILD_DUR
            });
        });

        WORD_FORM = units.reduce(function (m2, u) { return Math.max(m2, u.form); }, 0);

        /* Echte Ausdehnung des Schriftzugs. Die Aufloesung staffelt sich daran
           und nicht an der Bildmitte - sonst erreicht das aeusserste Band nie
           den Wert 0 und die Staffelung nutzt ihren Bereich nicht aus. */
        let lo = Infinity, hi = -Infinity;
        bars.forEach(function (b) {
            if (b.centerX < lo) { lo = b.centerX; }
            if (b.centerX > hi) { hi = b.centerX; }
        });
        WORD_MID  = (lo + hi) / 2;
        WORD_HALF = Math.max(1, (hi - lo) / 2);
    }

    /* --- Kamerafahrt -------------------------------------------------------
       Laeuft ueber die viewBox, nicht ueber eine CSS-Transform. Grund: die
       Buchstaben sind clipPaths aus Text. Skaliert man die umgebende Gruppe,
       muss der Browser diese Maske stufenweise neu rastern, und jede
       Neurasterung ist ein sichtbarer Schaerfesprung. Ueber die viewBox
       rendert die Szene in jedem Frame in voller Aufloesung neu.

       Waehrend der Standphase ist der berechnete String identisch, also wird
       setAttribute uebersprungen und der Browser rechnet gar nichts.

       Schein und Vignette liegen deshalb ausserhalb des SVG, aber weiterhin
       innerhalb der Schicht - sonst wuerden sie mitzoomen. Ueber den Rand
       hinaus zoomt die Kamera trotzdem; abgeschnitten wird das vom
       overflow: hidden des Containers. */
    function camScale(t) {
        if (t <= 0) { return CAM_FROM; }
        if (t < WORD_FORM) {
            return CAM_FROM + (CAM_HOLD - CAM_FROM) * easeOutFn(t / WORD_FORM);
        }
        if (t < EXIT_START) { return CAM_HOLD; }
        if (t < TOTAL) {
            return CAM_HOLD + (CAM_END - CAM_HOLD) * easeInFn((t - EXIT_START) / (TOTAL - EXIT_START));
        }
        return CAM_END;
    }

    /* Eine Nachkommastelle genuegt: ein Zehntel einer viewBox-Einheit ist
       auf jedem realen Bildschirm weit unter einem Pixel. Zwei Stellen
       erzeugten nur haeufiger einen neuen String und damit haeufiger ein
       setAttribute, das der Browser tatsaechlich verarbeiten muss. */
    function viewBoxAt(t) {
        const s = camScale(t);
        const w = VW / s, h = VH / s;
        return ((VW - w) / 2).toFixed(1) + " " + ((VH - h) / 2).toFixed(1) +
            " " + w.toFixed(1) + " " + h.toFixed(1);
    }

    function runCamera(t0) {
        let last = "";
        let holdDone = false;

        function apply(t) {
            const vb = viewBoxAt(t);
            if (vb !== last) { svg.setAttribute("viewBox", vb); last = vb; }
        }

        /* Waehrend der Standphase rechnet die Kamera absichtlich nicht mehr.
           Aendert sich in dieser Zeit das Fensterformat, muss sie einmal
           aufgeweckt werden - sonst bliebe der alte Ausschnitt bis zur
           Aufloesung stehen. */
        camRefresh = function () {
            last = "";
            holdDone = false;
        };

        function frame(now) {
            const t = now - t0;

            if (t < WORD_FORM || t >= EXIT_START) {
                apply(t);
                holdDone = false;
            } else if (!holdDone) {
                /* Standphase: der Wert ist ueber die gesamte Dauer konstant.
                   Einmal exakt auf den Endwert der Zufahrt setzen, danach in
                   jedem Frame nur noch die Abfrage oben - kein Rechnen, kein
                   String, kein Attributzugriff. */
                holdDone = true;
                apply(WORD_FORM);
            }

            if (t < TOTAL) { rafId = window.requestAnimationFrame(frame); }
        }
        rafId = window.requestAnimationFrame(frame);
    }

    /* --- Endzustand ohne Bewegung ------------------------------------------ */
    function settle() {
        window.cancelAnimationFrame(rafId);
        svg.setAttribute("viewBox", "0 0 " + VW + " " + VH);
        bars.forEach(function (b) {
            clearAll(b.node);
            b.node.setAttribute("fill", b.tint);
            b.node.style.transform = "none";
            b.node.style.opacity = "1";
        });
        /* Bei reduzierter Bewegung werden Lichtstreifen und Bloom nie
           gebraucht - display:none statt Deckkraft 0 spart das Rastern der
           Weichzeichnung vollstaendig. */
        units.forEach(function (u) {
            clearAll(u.sweep); u.sweep.style.display = "none";
            clearAll(u.bloom); u.bloom.style.display = "none";
        });
        clearAll(glow); glow.style.opacity = "0.2";
    }

    /* --- Sequenz ------------------------------------------------------------ */
    function animate() {
        window.cancelAnimationFrame(rafId);
        svg.setAttribute("viewBox", viewBoxAt(0));

        units.forEach(function (u) {
            u.sweep.style.display = "";
            u.bloom.style.display = "";
            u.sweep.style.opacity = "";
            u.bloom.style.opacity = "";
        });
        glow.style.opacity = "";

        /* will-change nur solange, wie sich wirklich etwas bewegt. Dauerhaft
           gesetzt haelt es eine eigene Compositing-Ebene ueber den ganzen
           Viewport offen, auch waehrend des Standbilds. */
        root.classList.add("is-playing");

        bars.forEach(function (b) {
            const node = b.node;
            clearAll(node);

            /* Ein- und Ausfahrt sind spiegelbildlich: gleiche Achse, gleicher
               Drift, gleiche Streckung - nur Richtung und Beschleunigung sind
               umgedreht. Das Band faehrt am Ende in der Richtung weiter, in der
               es hereingekommen ist. scaleY simuliert Bewegungsunschaerfe; ein
               echter Blur waere bei ueber 40 Elementen nicht bezahlbar. */
            const sign    = b.seed % 2 === 0 ? 1 : -1;
            const inDist  = 780 + rnd(b.seed) * 520;
            const driftIn = (rnd(b.seed + 100) - 0.5) * 110;
            const stretch = 2.4 + rnd(b.seed + 200) * 1.8;
            const jitter  = rnd(b.seed + 300) * BUILD_JITTER;

            node.animate([
                { transform: "translate(" + (-driftIn) + "px," + (-sign * inDist) + "px) scaleY(" + stretch + ")", opacity: 0 },
                { transform: "translate(0px,0px) scaleY(1)", opacity: 1 }
            ], {
                duration: BUILD_DUR,
                delay: b.start + b.dist * BUILD_STAGGER + jitter,
                easing: EASE_OUT,
                fill: "both"
            });

            node.animate([
                { fill: b.from },
                { fill: b.tint }
            ], {
                duration: TINT_DUR,
                delay: b.start + TINT_OFFSET + b.dist * TINT_STEP,
                easing: SOFT,
                fill: "both"
            });

            /* fill "forwards" statt "both": mit "both" wuerde der Rueckwaertsfill
               schon ab t=0 greifen und den kompletten Aufbau ueberschreiben. */
            const fromCenter = clamp01(Math.abs(b.centerX - WORD_MID) / WORD_HALF);

            node.animate([
                { transform: "translate(0px,0px) scaleY(1)", opacity: 1 },
                { transform: "translate(" + (driftIn * 1.8) + "px," + (sign * 1100) + "px) scaleY(" + (stretch * 1.4) + ")", opacity: 0 }
            ], {
                duration: EXIT_DUR,
                delay: EXIT_START + (1 - fromCenter) * EXIT_SPREAD,
                easing: EASE_IN,
                fill: "forwards"
            });
        });

        units.forEach(function (u) {
            /* Der Lichtstreifen gehoert zum Aufbau der jeweiligen Zeile und
               ist vorbei, bevor beide stehen. Waehrend der Standphase darf sich
               nichts mehr bewegen. */
            clearAll(u.sweep);
            const sweepAnim = u.sweep.animate([
                { transform: "translateX(0px)", opacity: 0 },
                { transform: "translateX(700px)", opacity: 1, offset: 0.4 },
                { transform: "translateX(2200px)", opacity: 0 }
            ], { duration: SWEEP_DUR, delay: u.start + SWEEP_OFFSET, easing: SOFT, fill: "both" });

            /* Nach dem Durchlauf aus dem Rendering nehmen. Deckkraft 0 heisst
               nur unsichtbar - gezeichnet und beim Zoom neu gerastert wuerde
               die Gruppe trotzdem in jedem weiteren Frame. */
            sweepAnim.onfinish = function () { u.sweep.style.display = "none"; };

            /* Bloom ist die Energie des Ankommens, nicht des Dastehens: er
               gipfelt kurz vor der Formung und ist danach weg. Als Plateau ueber
               die Standphase legte er einen weichen Schleier ueber das Standbild. */
            clearAll(u.bloom);
            const delay = u.start + BLOOM_OFFSET;
            const span  = (u.form + 80) - delay;
            const bloomAnim = u.bloom.animate([
                { opacity: 0,    offset: 0 },
                { opacity: 0.38, offset: clamp01((u.form - 150 - delay) / span) },
                { opacity: 0,    offset: 1 }
            ], { duration: span, delay: delay, easing: SOFT, fill: "both" });

            /* Der teuerste Posten der ganzen Sequenz. Die Weichzeichnung ist
               ein Gaussfilter ueber eine grosse Flaeche, und weil die Kamera
               die viewBox jeden Frame aendert, kann der Browser das Ergebnis
               nicht zwischenspeichern - er rechnet es jedes Mal neu.

               Der Bloom ist aber bereits vor dem Standbild vorbei. Ihn danach
               auf display:none zu setzen nimmt den Filter aus rund der Haelfte
               aller Frames heraus, ohne dass sich am Bild etwas aendert. */
            bloomAnim.onfinish = function () { u.bloom.style.display = "none"; };
        });

        /* Der Schein faellt bis zum Standbild auf einen leisen Sockel und haelt
           ihn dort exakt - gleiche Deckkraft an beiden Stuetzstellen, dazwischen
           kann die Interpolation nichts anderes liefern. Nur die Deckkraft
           animiert, kein transform: die Ebene ist promotet und muesste sonst
           neu gerastert werden. */
        clearAll(glow);
        const glowAnim = glow.animate([
            { opacity: 0,   offset: 0 },
            { opacity: 0.3, offset: clamp01(units[0].form / TOTAL) },
            { opacity: 0.2, offset: clamp01(WORD_FORM / TOTAL) },
            { opacity: 0.2, offset: clamp01(EXIT_START / TOTAL) },
            { opacity: 0.5, offset: 0.88 },
            { opacity: 0,   offset: 1 }
        ], { duration: TOTAL, easing: SOFT, fill: "both" });

        glowAnim.onfinish = function () { root.classList.remove("is-playing"); };

        /* Kamera erst starten, wenn die Startzeit der Keyframe-Animationen
           feststeht. startTime und der Zeitstempel von requestAnimationFrame
           teilen sich dieselbe Zeitbasis - damit laufen beide framegenau
           synchron statt bis zu einen Frame auseinander. */
        if (glowAnim.ready && typeof glowAnim.ready.then === "function") {
            glowAnim.ready.then(function () {
                runCamera(glowAnim.startTime === null ? performance.now() : glowAnim.startTime);
            }).catch(function () { runCamera(performance.now()); });
        } else {
            runCamera(performance.now());
        }
    }

    /* --- Schliessen ---------------------------------------------------------
       Die Schicht bleibt im DOM und wird ueber das Attribut hidden auf
       display:none gesetzt. Das ist mehr als nur unsichtbar: eine per
       opacity ausgeblendete Flaeche bliebe anklickbar und laege weiter
       ueber der Seite. display:none nimmt sie aus Layout und
       Accessibility-Baum heraus, ohne das Markup anzutasten.

       Fokusziele gibt es in der Schicht keine - sie enthaelt nur Grafik und
       ist fuer Tastatur und Screenreader ohnehin unsichtbar. */

    function scheduleDismiss() {
        window.clearTimeout(dismissTimer);
        dismissTimer = window.setTimeout(dismiss, (reduced ? REDUCED_HOLD : TOTAL) + END_HOLD);
    }

    function dismiss() {
        if (dismissed) { return; }
        dismissed = true;

        window.clearTimeout(dismissTimer);
        window.clearTimeout(loadTimer);
        window.cancelAnimationFrame(rafId);
        window.cancelAnimationFrame(resizeRaf);

        camRefresh = null;
        window.removeEventListener("resize", onResize);
        window.removeEventListener("orientationchange", onResize);

        root.classList.add("is-done");

        window.setTimeout(function () {
            /* Laufende Keyframe-Animationen abbrechen. Ein display:none
               Element wird zwar nicht mehr gezeichnet, die Animationen liefen
               aber weiter und haetten bei einem spaeteren Einblenden einen
               halb abgespielten Zustand hinterlassen. */
            if (typeof root.getAnimations === "function") {
                root.getAnimations({ subtree: true }).forEach(function (a) { a.cancel(); });
            }

            root.classList.remove("is-ready", "is-done", "is-playing");
            root.hidden = true;          /* -> display: none */

            unlockScroll();

            /* Doppelt gemoppelt: gesetzt wird der Merker bereits beim Start.
               Hier steht er noch einmal fuer den Fall, dass uebersprungen wird,
               bevor ueberhaupt etwas lief. markSeen() ist idempotent. */
            markSeen();
        }, FADE + 50);
    }

    /* --- Start -------------------------------------------------------------- */
    function run() {
        window.cancelAnimationFrame(rafId);

        if (reduced) { settle(); } else { animate(); }

        scheduleDismiss();
    }

    function autostart() {
        if (started || dismissed) { return; }
        started = true;

        /* Der Merker faellt beim Start, nicht erst beim Ausblenden. Sonst
           brachte ein Neuladen mitten in der Sequenz das Intro erneut - und
           genau das soll ausgeschlossen sein. Gesehen ist gesehen, auch
           halb. */
        markSeen();

        layout();
        run();
    }

    /* --- Vorhang auf ---------------------------------------------------------
       Gewartet wird nur auf die Schriften, nicht auf window.load.

       Das waere der groesste Posten der wahrgenommenen Ladezeit: load feuert
       erst, wenn jedes Bild, jedes Stylesheet und jedes Skript der Seite
       durch ist. Den Vorspann einer Seite ausgerechnet auf das
       vollstaendige Laden dieser Seite warten zu lassen, dreht die
       Reihenfolge um - er soll ja die Wartezeit fuellen, nicht an ihrem Ende
       stehen. */
    function whenFontsReady(next) {
        let done = false;
        function go() {
            if (done) { return; }
            done = true;
            window.clearTimeout(loadTimer);
            next();
        }

        loadTimer = window.setTimeout(go, FONT_TIMEOUT);

        /* Gewartet wird auf document.fonts.ready, nicht nur auf load(): load()
           loest aus, sobald die Datei da ist, ready erst, wenn der Font auch
           im Layout angewendet wurde. */
        if (document.fonts && document.fonts.load) {
            Promise.all([
                document.fonts.load("600 340px Roboto"),
                document.fonts.load("italic 700 340px Roboto")
            ])
                .then(function () { return document.fonts.ready; })
                .then(go)
                .catch(go);
        } else {
            go();
        }
    }

    /* Im Hintergrundtab pausiert requestAnimationFrame, die Zeitgeber der
       Web Animations API und setTimeout laufen aber weiter. Ohne diese
       Sperre liefe die Sequenz unsichtbar ab, der Merker fiele, und beim
       Zurueckwechseln waere das Intro "gesehen", ohne dass es jemand
       gesehen hat. Nebenbei spart es die Arbeit vollstaendig ein. */
    function whenVisible(next) {
        if (document.visibilityState !== "hidden") { next(); return; }

        document.addEventListener("visibilitychange", function onShow() {
            if (document.visibilityState === "hidden") { return; }
            document.removeEventListener("visibilitychange", onShow);
            if (!dismissed) { next(); }
        });
    }

    function reveal() {
        if (revealed || dismissed) { return; }
        revealed = true;

        /* lockScroll() steht nicht mehr hier, sondern beim Sichtbarmachen
           der Flaeche weiter oben - reveal() faellt erst nach den
           Schriften, und bis dahin soll die Seite dahinter schon still
           stehen. Der Aufruf ist idempotent, ein zweiter waere folgenlos. */
        root.classList.add("is-ready");

        whenVisible(function () {
            window.setTimeout(autostart, 150);
        });
    }

    whenFontsReady(function () {
        if (!dismissed) { reveal(); }
    });
}
